// components/workstation-message.js
// ── Single message row for the workstation chat panel ──
//
// WorkstationMessage     — wrapper with hover state, username cap, bubble,
//                          and toolbar (ReactionBar + ShiftStaticLogo).
// WorkstationMessageContent — fence-aware content renderer:
//   · complete fence → gold artifact card + stripped text
//   · partial fence (streaming) → animated "Building…" card
//   · no fence → MessageContent passthrough
//
// FENCE_OPEN_RE / FENCE_COMPLETE_RE are exported for use in
// the onTextToken callback inside the parent's useChatThread config.

import { useState } from 'react';
import MessageContent from './message-content';
import ReactionBar from './reaction-bar';
import { StreamingIndicator, ShiftStaticLogo } from './message-row';

// No 'g' flag — .test() with 'g' has stateful lastIndex that breaks in React renders
export const FENCE_OPEN_RE = /```(html|svg|markdown|python|javascript|js|css|json|sql|ts|tsx|jsx|py|code)\n/i;
export const FENCE_COMPLETE_RE = /```(?:html|svg|markdown|python|javascript|js|css|json|sql|ts|tsx|jsx|py|code)\n[\s\S]*?```/i;

// ── Fence-aware content ────────────────────────────────────
// Strips code fences from AI messages and shows a compact gold artifact
// card instead of raw source code. Works during streaming (partial
// fence → "Building...") and after (complete fence → card).
export function WorkstationMessageContent({ m, i }) {
  const content = typeof m.content === 'string' ? m.content : '';
  const isAssistant = m.role === 'assistant';

  // Non-assistant or empty → render normally
  if (!isAssistant || !content) return <MessageContent m={m} i={i} />;

  // Fence detection on the VISIBLE content (for rendering gold cards).
  // Canvas events are handled by onTextToken on the raw stream, not here.

  // Check for complete fenced blocks → replace with artifact cards
  const completeFences = content.match(FENCE_COMPLETE_RE);
  if (completeFences && completeFences.length > 0) {
    // Strip fenced blocks, keep text before/after
    const stripped = content.replace(FENCE_COMPLETE_RE, '').trim();
    const fenceType = completeFences[0].match(/```(\w+)/)?.[1] || 'artifact';
    const typeLabels = { html: 'Page', svg: 'SVG', markdown: 'Document', python: 'Code', javascript: 'Code', js: 'Code', css: 'Code', json: 'Data', sql: 'Code' };
    const typeLabel = typeLabels[fenceType.toLowerCase()] || 'Artifact';
    // Pick thumbnail bar colors by type
    const thumbColors = { Page: ['#c8a96e','#7a9e82','#c8a96e','#7a9e82'], SVG: ['#9b8fc4','#c8a96e','#9b8fc4','#c8a96e'], Document: ['#7a9e82','#7a9e82','#c8a96e','#7a9e82'], Code: ['#9b8fc4','#9b8fc4','#c8a96e','#9b8fc4'], Data: ['#c8a96e','#9b8fc4','#c8a96e','#9b8fc4'] };
    const bars = thumbColors[typeLabel] || thumbColors.Page;
    return (
      <>
        {stripped && <MessageContent m={{ ...m, content: stripped }} i={i} />}
        <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, border: '1px solid rgba(122,158,130,0.25)', background: 'rgba(122,158,130,0.08)', color: '#7a9e82', display: 'inline-block', marginTop: 3, fontFamily: 'Georgia, serif' }}>
          → canvas updated
        </span>
        <div style={{
          margin: '2px 0 2px 9px',
          padding: '5px 8px',
          background: '#111',
          border: '1px solid rgba(200,169,110,0.25)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          cursor: 'pointer',
        }}>
          <div style={{ width: 34, height: 24, background: '#07070a', border: '1px solid #1e1e2a', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 1.5, padding: 3 }}>
            {[55, 90, 40, 70].map((h, j) => (
              <div key={j} style={{ flex: 1, height: `${h}%`, background: `${bars[j]}77`, borderRadius: 1 }} />
            ))}
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif' }}>Canvas updated</div>
            <div style={{ fontSize: 9, color: '#333', fontFamily: 'Georgia, serif', marginTop: 1 }}>{typeLabel} — click to view</div>
          </div>
        </div>
      </>
    );
  }

  // Check for partial fence (streaming — fence opened but not yet closed)
  if (m.streaming && FENCE_OPEN_RE.test(content) && !FENCE_COMPLETE_RE.test(content)) {
    // Text before the fence opening
    const fenceIdx = content.search(FENCE_OPEN_RE);
    const beforeFence = content.slice(0, fenceIdx).trim();
    const fenceType = content.match(FENCE_OPEN_RE)?.[1] || 'artifact';
    const typeLabels = { html: 'Page', svg: 'SVG', markdown: 'Document', python: 'Code', javascript: 'Code', js: 'Code', css: 'Code', json: 'Data', sql: 'Code' };
    const typeLabel = typeLabels[fenceType.toLowerCase()] || 'artifact';
    return (
      <>
        {beforeFence && <MessageContent m={{ ...m, content: beforeFence }} i={i} />}
        <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, border: '1px solid rgba(200,169,110,0.25)', background: 'rgba(200,169,110,0.08)', color: '#c8a96e', display: 'inline-block', marginTop: 3, fontFamily: 'Georgia, serif' }}>
          canvas command
        </span>
        <div style={{
          margin: '2px 0 2px 9px',
          padding: '5px 8px',
          background: '#111',
          border: '1px solid rgba(200,169,110,0.15)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          animation: 'ws-artifact-building 1.5s ease-in-out infinite',
        }}>
          <div style={{ width: 34, height: 24, background: '#07070a', border: '1px solid #1e1e2a', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 10, color: '#c8a96e', animation: 'ws-artifact-spin 2s linear infinite', display: 'inline-block' }}>◎</span>
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif' }}>Building {typeLabel}...</div>
            <div style={{ fontSize: 9, color: '#444', fontFamily: 'Georgia, serif', fontStyle: 'italic', marginTop: 1 }}>Rendering on canvas when ready</div>
          </div>
        </div>
      </>
    );
  }

  // No fences — render normally
  return <MessageContent m={m} i={i} />;
}

// ── WorkstationMessage ─────────────────────────────────────
// Owns its own hover state so the reaction bar / static-logo only fades
// in for the message the cursor is on. Mirrors components/message-row.js
// but adapted to the workstation's tighter layout (88% max width, no
// border on AI bubbles, gold username caption above instead of inside).
export function WorkstationMessage({ m, i, isLastUser, lastUserMsgRef, userRecord, onPin, onRegenerate, onEdit }) {
  const [hovered, setHovered] = useState(false);
  const isUser = m.role === 'user';

  return (
    <div
      ref={isLastUser ? lastUserMsgRef : null}
      data-msg-role={m.role}
      data-msg-id={m.id || ''}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginBottom: 18,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <div style={{
        fontSize: 9,
        color: isUser ? '#888' : '#c8a96e',
        marginBottom: 4,
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {isUser ? (userRecord?.first_name || 'You') : (userRecord?.ai_name || 'Shift')}
      </div>

      {/* Message bubble */}
      <div style={{
        maxWidth: '88%',
        fontSize: 'var(--msg-body-size, 14px)',
        lineHeight: 1.7,
        color: 'var(--text-1)',
        fontFamily: 'var(--font-serif)',
        background: isUser ? '#111118' : 'none',
        border: isUser ? '1px solid #252018' : 'none',
        borderRadius: isUser ? '12px 12px 2px 12px' : 0,
        padding: isUser ? '10px 14px' : '0',
        whiteSpace: 'pre-wrap',
      }}>
        {/* AI: streaming pulse before any tokens. Static logo + toolbar
            after streaming finishes. While streaming with content already
            present, show content + a small inline pulse so the user sees
            that more is coming. */}
        {!isUser && m.streaming && !m.content ? (
          <StreamingIndicator />
        ) : !isUser && m.streaming && m.content ? (
          <>
            <WorkstationMessageContent m={m} i={i} />
            <StreamingIndicator />
          </>
        ) : (
          <WorkstationMessageContent m={m} i={i} />
        )}
      </div>

      {/* Toolbar — hover-reveal. AI also gets the static logo below. */}
      {(!m.streaming || isUser) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          marginTop: 2,
        }}>
          <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
            <ReactionBar
              message={m}
              onCopy={() => {
                try {
                  const text = typeof m.content === 'string' ? m.content : '';
                  // Strip file markers from copied text so users get clean prose
                  const cleaned = text
                    .replace(/^\[img:[^\]]+\]\s*/, '')
                    .replace(/^\[file:[^\]]+\]\s*/, '');
                  navigator.clipboard.writeText(cleaned);
                } catch (_) { /* clipboard API may be blocked */ }
              }}
              onPin={() => onPin?.(m)}
              onIdea={() => {}}
              onRegenerate={() => onRegenerate?.(m)}
              onFlag={() => {}}
              onEdit={() => onEdit?.(m)}
            />
          </div>
          {!isUser && <ShiftStaticLogo userRecord={userRecord} />}
        </div>
      )}
    </div>
  );
}
